import axios from "axios";
import { useContext, useEffect, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import styled from "styled-components";
import theme from "../styles/theme";
import { PillarType } from "../types/Roadmap";
import Button from "./Button";
import FlexBox, { FlexGrow } from "./FlexBox";
import Input, { InputTextArea } from "./Input";
import MinimalSelect from "./MinimalSelect";
import { StyledText } from "./Text";
import { Checkbox, Icon as MuiIcon } from "@mui/material";
import { components } from "react-select";
import Icon from "./Icon";
import Status from "../types/enum/Status";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { VersionContext } from "../context/VersionContext";
import { convertToSelectCombine } from "../hooks/useScript";
import { TOption } from "../types/Options";

const { Option, ValueContainer, SingleValue } = components;
const IconOption = (props: any) => (
  <Option {...props}>
    <MuiIcon
      sx={{
        mr: 1,
        padding: "4px",
        background: props.data.graphics.iconbg,
        boxShadow: props.data.graphics.iconshadow,
        borderRadius: "100px",
        verticalAlign: "bottom",
        fontSize: "14px",
      }}
    >
      {props.data.graphics.icon}
    </MuiIcon>
    {props.data.label}
  </Option>
);
const CustomSingleValue = (props: any) => {
  return (
    <SingleValue {...props}>
      <MuiIcon
        sx={{
          mr: 1,
          padding: "4px",
          background: props.data.graphics.iconbg,
          boxShadow: props.data.graphics.iconshadow,
          borderRadius: "100px",
          fontSize: "14px",
          verticalAlign: "sub",
        }}
      >
        {props.data.graphics.icon}
      </MuiIcon>
      <span className="react-select__single-value">
        {props.selectProps.getOptionLabel(props.data)}
      </span>
    </SingleValue>
  );
};
export const CustomFlexFixed = styled(FlexBox)`
  width: 100%;
  position: fixed;
  bottom: 0;
  right: 0px;
  background: #ffffff;
  padding: 6px 0px;
  box-shadow: 0 75px 45px 45px black;
  // z-index: 9999;
`;
const CustomFlexPlain = styled(FlexBox)`
  padding: 20px;
  align-items: flex-start;
`;
const CustomGrowPlain = styled(FlexGrow)`
  min-width: 150px;
  flex-grow: 0;
`;
const Label = styled.h5`
  text-transform: uppercase;
  color: ${theme.color.darkBlueGray900};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: left;
`;
const LabelData = styled(StyledText)`
  color: ${theme.color.lightBlueGray500};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  vertical-align: text-top;
`;
type CourseOverviewCreateType = {
  data: any;
  pillar?: any;
  subdomain_id?: any;
  area_id?: any;
  core_technical?: boolean;
  math_science?: boolean;
  social_communication_arts?: boolean;
  research?: boolean;
  showBackButton?: boolean;
  goBack: () => void;
  onSaveComplete: (data: any) => void;
};
const schema = yup
  .object({
    identity_code: yup.string().required(),
    title: yup.string().required(),
    description: yup.string(),
    pillar_id: yup.string().required(),
    alt_pillar_id: yup.string().required(),
    required: yup.boolean().required(),
    // pass_fail: yup.boolean().required(),
    min_optional_count: yup.number().required(),
    competency_code: yup
      .string()
      .matches(/^(([A-Z]{3})([-.][0-9]{3}))?$/, "Incorrect format!")
      .required("This field is required"),
    credits: yup
      .number()
      .typeError("This field is required and must be numeric")
      .required(),
    instructor_id: yup.string().required("Instructor is a required field"),
    university_code: yup.string().required(),
    prerequisities: yup.array(
      yup.object({
        id: yup.string().required("Select a competency from list"),
        title: yup.string(),
      })
    ),

    core_technical: yup.boolean(),
    math_science: yup.boolean(),
    social_communication_arts: yup.boolean(),
    research: yup.boolean(),
    soft_skill: yup.boolean(),
    xp: yup.boolean(),

    subdomain_id: yup.string(),
    area_id: yup.string(),
  })
  .test(
    "at-least-one-true",
    "At least one of the field must be selected",
    function (value) {
      if (
        !(
          value.core_technical ||
          value.math_science ||
          value.social_communication_arts ||
          value.research ||
          value.soft_skill ||
          value.xp
        )
      ) {
        return this.createError({ path: "core_technical" });
      }
      return true;
    }
  )
  .required();
const SaveButton = styled(Button)`
  background: ${({ theme }) => theme.color.blueButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;
export const CancelButton = styled(Button)`
  margin-right: 10px;
  vertical-align: bottom;
  background: ${({ theme }) => theme.color.deleteButton};
  box-shadow: 0px 2px 5px #808080cc;
  border: none;
  width: 120px;
  min-height: 40px;
`;
function CourseOverviewCreate({
  data,
  subdomain_id,
  pillar,
  area_id,
  goBack,
  showBackButton = false,
  onSaveComplete,
}: CourseOverviewCreateType) {
  const navigate = useNavigate();
  const {
    register: registerOverview,
    handleSubmit: handleSubmitOverview,
    control: controlOverview,
    watch: watchOverview,
    reset: resetOverview,
    setValue: setValueOverview,
    formState: { errors: errorsOverview },
  } = useForm({ mode: "onSubmit", resolver: yupResolver(schema) });

  const { version } = useContext(VersionContext);
  const [instructors, setInstructors] = useState<Array<any>>([]);
  const [count, setCount] = useState({
    competency_code: 0,
    title: 0,
    description: 0,
    identity_code: 0,
  });

  const [pillars, setPillars] = useState<
    Array<{
      label: string;
      value: string;
      selected: boolean;
      index: string;
      graphics: any;
    }>
  >([]);
  const onSubmitOverview = (data: any) =>
    onSaveComplete({
      ...data,
      area_map: {
        core_technical: data.core_technical,
        math_science: data.math_science,
        social_communication_arts: data.social_communication_arts,
        research: data.research,
        soft_skill: data.soft_skill,
        xp: data.xp,
      },
    });
  useEffect(() => {
    // resetOverview();
    if (data && data.id) {
      mapData(data);
    }
    setValueOverview("alt_pillar_id", pillar.value);
    setValueOverview("min_optional_count", 0);
    setValueOverview("university_code", version.university);
    if (pillar) {
      setValueOverview("pillar_id", pillar.value);
    }
    if (subdomain_id) {
      setValueOverview("subdomain_id", subdomain_id.value);
    }
    if (area_id) {
      setValueOverview("area_id", area_id.value);
    }
  }, [data, pillar, subdomain_id, area_id]);
  const mapData = (data: any) => {
    setValueOverview("pillar_id", data.id);
    setValueOverview("subdomain_id", data.id);
    setValueOverview("area_id", data.id);
  };
  useEffect(() => {
    fetchPillars();
    fetchInstructors();
  }, []);
  const fetchPillars = () => {
    axios
      .get("/pillar")
      .then((res) => {
        mapPillars(res.data.pillars);
      })
      .catch((err:any) => {
        if (err?.response?.data?.message === "missing or malformed jwt") {
          window.location.pathname="/signin";
        }
      });
  };
  const fetchInstructors = () => {
    axios
      .get(`/instructor`)
      .then((res) => {
        if (res.data.instructors) {
          setInstructors(
            convertToSelectCombine(res.data.instructors, "title", "name", "id")
          );
        }
      })
      .catch(() => {
        setInstructors([]);
      });
  };
  const mapPillars = (data: PillarType) => {
    const custom: Array<{
      label: string;
      value: string;
      selected: boolean;
      index: string;
      graphics: any;
    }> = [];
    data.forEach((element: any, index: any) => {
      custom.push({
        value: element.id,
        label: element.title,
        selected: false,
        index: "",
        graphics: element.graphics,
      });
    });
    setPillars(custom);
  };
  const resetRemovedPillar = (index: any) => {
    const found = filterArrayElement(index);
    if (found) {
      setPillars(
        pillars.map((itemfound) =>
          itemfound.value === found.value
            ? { ...itemfound, index: "", selected: false }
            : itemfound
        )
      );
    }
  };
  const updateSelectedPillars = (val: any, index: any) => {
    const found = filterArrayElement(index);
    if (found) {
      setPillars(
        pillars.map((itemfound) =>
          itemfound.value === found.value
            ? { ...itemfound, index: "", selected: false }
            : itemfound.value === val.value
            ? { ...itemfound, index: String(index), selected: true }
            : itemfound
        )
      );
    } else {
      setPillars(
        pillars.map((item) =>
          item.value === val.value
            ? { ...item, index: String(index), selected: true }
            : item
        )
      );
    }
  };
  function filterArrayElement(index: string) {
    return pillars.filter((element) => {
      return String(element.index) === String(index);
    })[0];
  }
  return (
    <>
      <form onSubmit={handleSubmitOverview(onSubmitOverview)}>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pillars</Label>
          </CustomGrowPlain>
          {data && data.id && !pillar ? (
            <FlexGrow>
              <Controller
                name="pillar_id"
                control={controlOverview}
                render={({ field }) => (
                  <MinimalSelect
                    {...field}
                    isDisabled
                    onChange={() => {}}
                    value={pillars.filter((el) => el.value === field.value)}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        color: theme.color.lightBlueGray800,
                      }),
                    }}
                    options={pillars}
                    components={{
                      Option: IconOption,
                      // ValueContainer: ValueContainerLabel,
                      SingleValue: CustomSingleValue,
                    }}
                  />
                )}
              />
            </FlexGrow>
          ) : (
            <FlexGrow>
              <MuiIcon
                sx={{
                  mr: 1,
                  padding: "4px",
                  background: pillar.graphics.iconbg,
                  boxShadow: pillar.graphics.iconshadow,
                  borderRadius: "100px",
                  fontSize: "16px",
                  verticalAlign: "sub",
                }}
              >
                {pillar.graphics.icon}
              </MuiIcon>
              <LabelData>{pillar.label}</LabelData>
            </FlexGrow>
          )}
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Subdomain</Label>
          </CustomGrowPlain>
          {data && data.id && !subdomain_id ? (
            <FlexGrow>
              <Controller
                name="subdomain_id"
                control={controlOverview}
                render={({ field }) => (
                  <MinimalSelect
                    {...field}
                    isDisabled
                    onChange={() => {}}
                    options={pillars}
                    menuPortalTarget={document.body}
                    value={pillars.filter((el) => el.value === field.value)}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        color: theme.color.lightBlueGray800,
                      }),
                    }}
                    components={{
                      Option: IconOption,
                      // ValueContainer: ValueContainerLabel,
                      SingleValue: CustomSingleValue,
                    }}
                  />
                )}
              />
            </FlexGrow>
          ) : (
            <FlexGrow>
              <LabelData>{subdomain_id.label}</LabelData>
            </FlexGrow>
          )}
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Knowledge Area</Label>
          </CustomGrowPlain>
          {data && data.id && !area_id ? (
            <FlexGrow>
              <Controller
                name="area_id"
                control={controlOverview}
                render={({ field }) => (
                  <MinimalSelect
                    {...field}
                    isDisabled
                    menuPortalTarget={document.body}
                    onChange={() => {}}
                    value={pillars.filter((el) => el.value === field.value)}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        color: theme.color.lightBlueGray800,
                      }),
                    }}
                    options={pillars}
                    components={{
                      Option: IconOption,
                      // ValueContainer: ValueContainerLabel,
                      SingleValue: CustomSingleValue,
                    }}
                  />
                )}
              />
            </FlexGrow>
          ) : (
            <FlexGrow>
              <LabelData>{area_id.label}</LabelData>
            </FlexGrow>
          )}
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>
              Competency Code <span style={{ color: "red" }}> *</span>
            </Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="competency_code"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <Input
                        placeholder={`${pillar.prefix}-XXX`}
                        isFullWidth
                        maxLength={32}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            competency_code: e.target.value.length,
                          });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.competency_code}/32
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            {errorsOverview.competency_code && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.competency_code?.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>
              Identity Code <span style={{ color: "red" }}> *</span>
            </Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="identity_code"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <Input
                        // placeholder={`${pillar.prefix}-XXX`}
                        isFullWidth
                        maxLength={64}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            identity_code: e.target.value.length,
                          });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.identity_code}/64
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            {errorsOverview.identity_code && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>
              Title <span style={{ color: "red" }}> *</span>
            </Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="title"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <Input
                        isFullWidth
                        maxLength={256}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({ ...count, title: e.target.value.length });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.title}/256
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
              </FlexGrow>
            </FlexBox>
            {errorsOverview.title && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Description</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <FlexBox
              space={0}
              alignItems="start"
              style={{ width: "100%" }}
              direction="column"
            >
              <FlexGrow style={{ width: "100%" }}>
                <Controller
                  name="description"
                  control={controlOverview}
                  render={({ field }) => (
                    <>
                      <InputTextArea
                        isFullWidth
                        maxLength={2048}
                        value={field.value}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setCount({
                            ...count,
                            description: e.target.value.length,
                          });
                        }}
                      />
                      <FlexGrow style={{ width: "60%", textAlign: "end" }}>
                        <StyledText
                          style={{ fontSize: "14px" }}
                          color={theme.color.lightBlueGray500}
                        >
                          {count.description}/2048
                        </StyledText>
                      </FlexGrow>
                    </>
                  )}
                />
              </FlexGrow>
            </FlexBox>
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>
              Credits <span style={{ color: "red" }}> *</span>
            </Label>
          </CustomGrowPlain>
          <FlexGrow>
            <Input
              type="number"
              max={999999}
              min={0}
              isFullWidth
              {...registerOverview("credits", { required: true })}
              defaultValue={4}
            />
            {errorsOverview.credits && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.credits.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Required</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <input
              {...registerOverview("required", { required: true })}
              type="radio"
              id="yes"
              value="true"
              style={{ marginRight: "4px" }}
            />
            <LabelData
              style={{ marginRight: "16px" }}
              color={theme.color.lightBlueGray500}
            >
              Yes
            </LabelData>
            <input
              {...registerOverview("required", { required: true })}
              type="radio"
              id="no"
              value="false"
              style={{ marginRight: "4px" }}
              defaultChecked
            />
            <LabelData color={theme.color.lightBlueGray500}>No</LabelData>
            {errorsOverview.required && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>

        {/* <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Pass/Fail</Label>
          </CustomGrowPlain>
          <FlexGrow>
            <input
              {...registerOverview("pass_fail", { required: true })}
              type="radio"
              id="true"
              value="true"
              style={{ marginRight: "4px" }}
            />
            <LabelData
              style={{ marginRight: "16px" }}
              color={theme.color.lightBlueGray500}
            >
              Yes
            </LabelData>
            <input
              {...registerOverview("pass_fail", { required: true })}
              type="radio"
              id="false"
              value="false"
              style={{ marginRight: "4px" }}
              defaultChecked
            />
            <LabelData color={theme.color.lightBlueGray500}>No</LabelData>
            {errorsOverview.required && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                This field is required
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain> */}

        <CustomFlexPlain>
          <CustomGrowPlain>
            <Label>Area Map</Label>
          </CustomGrowPlain>
          <FlexGrow style={{ width: "100%" }}>
            <div
              style={{
                background: theme.color.blue50,
                width: "60%",
                borderRadius: 5,
                padding: "15px 0px",
              }}
            >
              <div style={{ minWidth: "100%", paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("core_technical", { required: true })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  Core Technical
                </LabelData>
              </div>
              <div style={{ minWidth: "100%", paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("math_science", { required: true })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  Math Science
                </LabelData>
              </div>

              <div style={{ minWidth: "60%", paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("social_communication_arts", {
                    required: true,
                  })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e.target.checked);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  Social Communication Arts
                </LabelData>
              </div>
              <div style={{ paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("research", { required: true })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  Research
                </LabelData>
              </div>
              <div style={{ paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("soft_skill", { required: true })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  Soft Skill
                </LabelData>
              </div>
              <div style={{ paddingLeft: 15 }}>
                <input
                  type="checkbox"
                  {...registerOverview("xp", { required: true })}
                  // checked={false}
                  onChange={(e: any) => {
                    console.log(e);
                  }}
                />
                <LabelData
                  style={{ marginLeft: "16px" }}
                  color={theme.color.lightBlueGray500}
                >
                  XP
                </LabelData>
              </div>
            </div>
            {errorsOverview.core_technical && (
              <p
                style={{
                  marginTop: 0,
                  color: theme.color.red800,
                }}
              >
                {errorsOverview.core_technical.message}
              </p>
            )}
          </FlexGrow>
        </CustomFlexPlain>
        <CustomFlexPlain style={{ marginBottom: "100px" }}>
          <CustomGrowPlain>
            <Label>
              Responsible instructor <span style={{ color: "red" }}> *</span>
            </Label>
          </CustomGrowPlain>
          {instructors && (
            <FlexGrow>
              <Controller
                name="instructor_id"
                control={controlOverview}
                render={({ field }) => (
                  <MinimalSelect
                    {...field}
                    value={instructors.filter((el) => el.id === field.value)}
                    onChange={(data: any) => {
                      field.onChange(data.value);
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                        color: theme.color.lightBlueGray800,
                      }),
                      control: (base) => ({
                        ...base,
                        width: "75%",
                      }),
                    }}
                    options={instructors}
                  />
                )}
              />
              {errorsOverview.instructor_id && (
                <p
                  style={{
                    marginTop: 0,
                    color: theme.color.red800,
                  }}
                >
                  {errorsOverview.instructor_id.message}
                </p>
              )}
            </FlexGrow>
          )}
        </CustomFlexPlain>
        <CustomFlexFixed>
          {showBackButton && (
            <Button
              key={0}
              style={{ marginLeft: "30px" }}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.primary}
              isOutline
            >
              <Icon style={{ paddingRight: "8px" }} name={"arrow_back"} /> Back
            </Button>
          )}
          {errorsOverview && (
            <p
              style={{
                marginTop: 0,
                color: theme.color.red800,
              }}
            ></p>
          )}
          <FlexGrow
            style={{
              textAlign: "end",
              marginRight: "30px",
              paddingTop: 10,
              paddingBottom: 10,
            }}
          >
            <CancelButton
              className="reduce_opct_onhover"
              style={{
                background: theme.color.deleteButton,
                boxShadow: "0 2px 5px #808080cc",
                border: "none",
                width: 120,
                minHeight: 40,
              }}
              key={1}
              type="button"
              onClick={() => {
                goBack();
              }}
              status={Status.danger}
              isOutline={false}
            >
              Cancel
            </CancelButton>
            <SaveButton
              className="reduce_opct_onhover"
              style={{
                background: theme.color.blueButton,
                boxShadow: "0 2px 5px #808080cc",
                border: "none",
                width: 120,
                minHeight: 40,
              }}
              key={2}
              type="submit"
              status={Status.primary}
              isOutline={false}
            >
              <Icon style={{ paddingRight: "8px" }} name={"save"} /> Save
            </SaveButton>
          </FlexGrow>
        </CustomFlexFixed>
      </form>
    </>
  );
}
export default CourseOverviewCreate;
